/**
 * Predictive search
 *
 * Search-as-you-type panel for the header search field (`snippets/predictive-search.liquid`).
 *
 * Each query runs two requests in parallel:
 * - Typesense for products, typo-tolerant and grouped by metal like the results page, so
 *   "View all N results" matches what the results page shows.
 * - Shopify predictive search (`/search/suggest.json`) for suggested searches and categories,
 *   which come with their real titles. It also supplies the products when Typesense is off
 *   or fails.
 *
 * The input is a combobox: arrow keys move through the options, Enter opens the active one
 * (or submits the form as before), Escape closes the panel.
 *
 * Loaded as a module by `snippets/predictive-search.liquid`, after `bundle.js`, whose
 * `window.theme.debounce` and `window.theme.money` it uses. `@theme/typesense` is imported
 * only when Typesense is enabled, so the panel does not load it otherwise.
 */

/**
 * @returns {Promise<?Object>} The Typesense module, or null when Typesense is off
 */
const loadTypesense = () => window.theme.typesense ? import('@theme/typesense') : Promise.resolve(null);

const MIN_LENGTH = 2;
const DELAY = 150;
const PRODUCT_LIMIT = 5;
const SUGGESTION_LIMIT = 6;
const RECENT_KEY = 'allurez:recent-searches';
const RECENT_LIMIT = 5;

/**
 * Escape text for HTML, keeping the bare `<mark>` (Typesense: the matched words) and `<span>`
 * (Shopify suggestions: the words completed after what was typed) tags.
 *
 * @param {string} value
 * @returns {string}
 */
const highlight = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/&lt;(\/?)(mark|span)&gt;/g, '<$1$2>');

/**
 * Same normalisation as `filter.js#getSkuSearchKey`: "IR229" or "1234-b", otherwise null.
 *
 * @param {string} terms
 * @returns {?string}
 */
const skuKey = (terms) => {
  const value = String(terms || '').trim();
  const isAlphanumericSku = /^[a-z][a-z0-9]*(?:[-_.][a-z0-9]+)*$/i.test(value) && /\d/.test(value);
  const isNumericSku = /^\d{3,}(?:[-_.][a-z0-9]+)*$/i.test(value);

  return isAlphanumericSku || isNumericSku ? value.toLowerCase() : null;
};

/**
 * Ask Shopify CDN for a smaller image, keeping the URL's version parameter.
 *
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
const resizeImage = (src, width) => {
  if (!src) return '';

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);

    return url.toString();
  } catch (error) {
    return src;
  }
};

const searchUrl = (terms) => `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}search?q=${encodeURIComponent(terms)}`;

const recentSearches = {
  get: () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');

      return Array.isArray(list) ? list.filter(item => typeof item === 'string') : [];
    } catch (error) {
      return [];
    }
  },

  add: (terms) => {
    const value = String(terms || '').trim();

    if (value.length < MIN_LENGTH) return;

    const list = [value, ...recentSearches.get().filter(item => item.toLowerCase() !== value.toLowerCase())].slice(0, RECENT_LIMIT);

    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (error) {
      // Private mode or storage disabled: recent searches just are not kept.
    }
  },

  clear: () => {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch (error) {
      // Nothing to clear.
    }
  }
};

class PredictiveSearch extends HTMLElement {

  connectedCallback() {

    this.input = document.getElementById(this.dataset.input);

    if (!this.input) return;

    this.form = this.input.form;
    this.header = this.closest('.main-header');
    this.listbox = this.querySelector('[role="listbox"]');
    this.scroller = this.querySelector('[data-scroll]');
    this.status = this.querySelector('[data-status]');
    this.cache = new Map();
    this.terms = '';
    this.requestId = 0;
    this.optionId = 0;

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.listbox.id);

    this.onInput = window.theme.debounce.apply(() => this.update(), DELAY);

    this.input.addEventListener('input', () => {
      if (!this.isOpen) this.open();
      this.setLoading(this.input.value.trim().length >= MIN_LENGTH);
      this.onInput();
    });
    this.input.addEventListener('focus', () => this.open());
    // Reopen after Escape without leaving the field.
    this.input.addEventListener('click', () => this.open());
    this.input.addEventListener('keydown', event => this.onKeydown(event));

    if (this.form) {
      this.form.addEventListener('submit', () => recentSearches.add(this.input.value));
    }

    // Clicking a search term counts as a search.
    this.addEventListener('click', event => {
      const term = event.target.closest('[data-search-term]');

      if (term) recentSearches.add(term.dataset.searchTerm);

      if (event.target.closest('[data-clear-recent]')) {
        recentSearches.clear();
        this.renderRecent();
        this.input.focus();
      }
    });

    // Keep the panel open while focus moves between the field and the panel.
    // The click that focused the field (the header's search icon) is not a click away from it.
    this.onDocumentClick = (event) => {
      if (this.contains(event.target) || event.target === this.input || document.activeElement === this.input) return;

      this.close();
    };
    document.addEventListener('click', this.onDocumentClick);

    window.addEventListener('resize', () => this.fitHeight());

    // Dims the page under the header, like the mini cart's drawer overlay. Clicking it is a click
    // outside the panel, so it closes.
    this.overlay = document.createElement('div');
    this.overlay.className = 'predictive-search-overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.overlay);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);

    if (this.overlay) this.overlay.remove();
  }

  get isOpen() {
    return !this.classList.contains('hidden');
  }

  open() {
    if (this.isOpen) return;

    this.update();
    this.classList.remove('hidden');

    // Nothing to show before typing (no recent searches, no popular searches set up).
    if (this.input.value.trim().length < MIN_LENGTH && !this.options.length) {
      this.classList.add('hidden');
      return;
    }

    this.input.setAttribute('aria-expanded', 'true');
    this.overlay.classList.add('is-visible');
    this.coverMenu();
    // The page behind stays put while the panel scrolls, as with the mini cart.
    document.documentElement.style.overflow = 'hidden';
    this.fitHeight();
  }

  close() {
    if (!this.isOpen) return;

    this.classList.add('hidden');
    this.input.setAttribute('aria-expanded', 'false');
    this.overlay.classList.remove('is-visible');
    this.header && this.header.classList.remove('search-open');
    this.style.top = '';
    document.documentElement.style.overflow = '';
    this.setActive(null);
  }

  /**
   * On desktop the panel opens over the menu row, right under the field. The row keeps its space
   * (only hidden), so the header's height and the page under it do not move.
   */
  coverMenu() {
    if (!this.header) return;

    this.header.classList.add('search-open');

    const menu = this.header.querySelector('#main-menu');

    if (!menu || !window.matchMedia('(min-width: 1024px)').matches) return;

    this.style.top = `${menu.getBoundingClientRect().top - this.header.getBoundingClientRect().top}px`;
  }

  /**
   * The panel scrolls inside itself so it never runs past the bottom of the screen.
   */
  fitHeight() {
    if (!this.isOpen) return;

    const top = this.getBoundingClientRect().top;

    this.scroller.style.maxHeight = `${Math.max(window.innerHeight - top, 200)}px`;
  }

  setLoading(loading) {
    this.toggleAttribute('data-loading', loading);
  }

  /**
   * Show one of the panel states: `empty`, `results` or `no-results`.
   *
   * @param {string} name
   */
  showState(name) {
    this.querySelectorAll('[data-state]').forEach(state => {
      const visible = state.dataset.state === name;

      state.classList.toggle('hidden', !visible);
      state.classList.toggle('flex', visible);
    });

    this.setActive(null);
  }

  async update() {

    const terms = this.input.value.trim();

    if (terms.length < MIN_LENGTH) {
      this.terms = '';
      this.setLoading(false);
      this.renderRecent();
      this.showState('empty');
      this.status.textContent = '';
      return;
    }

    if (terms === this.terms) {
      this.setLoading(false);
      return;
    }

    const requestId = ++this.requestId;

    let results;

    try {
      results = await this.fetchResults(terms);
    } catch (error) {
      console.error('Predictive search failed', error);
      results = { products: [], suggestions: [], collections: [], total: null };
    }

    // A slower answer for an older query must not replace a newer one.
    if (requestId !== this.requestId) return;

    this.terms = terms;
    this.setLoading(false);
    this.render(terms, results);
  }

  /**
   * @param {string} terms
   * @returns {Promise<{products: Array, suggestions: Array, collections: Array, total: ?number}>}
   */
  fetchResults(terms) {

    const key = terms.toLowerCase();

    if (!this.cache.has(key)) {
      const request = this.request(terms);

      this.cache.set(key, request);
      // Do not keep a failed request: the next keystroke should try again.
      request.catch(() => this.cache.delete(key));
    }

    return this.cache.get(key);
  }

  async request(terms) {

    const typesense = await loadTypesense().catch(error => {
      console.error('Typesense module failed to load, using Shopify products', error);
      return null;
    });
    const useTypesense = !!(typesense && typesense.default.config && typesense.default.config.host);

    const [products, shopify] = await Promise.all([
      useTypesense ? this.fetchTypesense(typesense, terms).catch(error => {
        console.error('Typesense predictive search failed, using Shopify products', error);
        return null;
      }) : Promise.resolve(null),
      this.fetchShopify(terms, !useTypesense).catch(error => {
        console.error('Shopify predictive search failed', error);
        return { suggestions: [], collections: [], products: [] };
      })
    ]);

    if (products) {
      return { ...shopify, products: products.items, total: products.total };
    }

    // Typesense is off or failed: Shopify's products, fetched now if they were not asked for.
    const fallback = useTypesense ? await this.fetchShopify(terms, true).catch(() => shopify) : shopify;

    return { ...shopify, products: fallback.products, total: null };
  }

  async fetchTypesense({ default: typesense, currencyRate, SEARCH_FIELDS, SEARCH_SORT }, terms) {

    const [result] = await typesense.search([{
      q: terms,
      // Same fields as the results page, so the products here are the first ones there.
      query_by: SEARCH_FIELDS,
      filter_by: 'clearance:=false && seo_hidden:=false',
      sort_by: SEARCH_SORT,
      group_by: 'metal_group',
      group_limit: 1,
      per_page: PRODUCT_LIMIT,
      include_fields: 'handle,title,image,image_alt,price_min,price_max,tags',
      highlight_fields: 'title',
      highlight_full_fields: 'title'
    }]);

    const hits = (result.grouped_hits || []).map(group => group.hits && group.hits[0]).filter(Boolean);
    const sku = skuKey(terms);

    // A style number lands on that exact product first.
    if (sku) {
      const exact = (hit) => (hit.document.tags || []).some(tag => tag.trim().toLowerCase() === sku);

      hits.sort((a, b) => exact(b) - exact(a));
    }

    const rate = currencyRate();

    return {
      total: result.found || 0,
      items: hits.map(hit => {
        const doc = hit.document;
        const title = hit.highlight && hit.highlight.title && hit.highlight.title.value;

        return {
          url: `/products/${doc.handle}`,
          title: doc.title,
          titleHtml: title ? highlight(title) : highlight(doc.title),
          image: doc.image,
          imageAlt: doc.image_alt || doc.title,
          price: window.theme.money.format(Math.round(doc.price_min * rate * 100)),
          from: doc.price_max > doc.price_min
        };
      })
    };
  }

  async fetchShopify(terms, withProducts) {

    const types = ['query', 'collection'];

    if (withProducts) types.push('product');

    const params = new URLSearchParams({
      q: terms,
      'resources[type]': types.join(','),
      'resources[limit]': Math.max(SUGGESTION_LIMIT, PRODUCT_LIMIT),
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });

    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const response = await fetch(`${root}search/suggest.json?${params}`);

    if (!response.ok) {
      throw new Error(`Shopify predictive search failed (${response.status})`);
    }

    const results = ((await response.json()).resources || {}).results || {};
    const typed = terms.toLowerCase();

    return {
      // The typed text itself is not a useful suggestion.
      suggestions: (results.queries || [])
        .filter(query => query.text.toLowerCase() !== typed)
        .slice(0, SUGGESTION_LIMIT)
        .map(query => ({ text: query.text, html: highlight(query.styled_text || query.text), url: searchUrl(query.text) })),
      // Several collections can share a title (e.g. "Sapphire Engagement Rings"); list it once.
      collections: (results.collections || [])
        .filter((collection, index, list) => list.findIndex(item => item.title.toLowerCase() === collection.title.toLowerCase()) === index)
        .slice(0, SUGGESTION_LIMIT)
        .map(collection => ({ text: collection.title, url: collection.url })),
      products: (results.products || [])
        // Clearance products show only on the clearance collection, never in search.
        .filter(product => !(product.tags || []).some(tag => tag.toLowerCase() === 'clearance'))
        .slice(0, PRODUCT_LIMIT)
        .map(product => ({
          url: product.url,
          title: product.title,
          titleHtml: highlight(product.title),
          image: product.featured_image && product.featured_image.url || product.image,
          imageAlt: product.featured_image && product.featured_image.alt || product.title,
          // Already in the shopper's currency.
          price: window.theme.money.format(Math.round(parseFloat(product.price_min || product.price) * 100)),
          from: parseFloat(product.price_max) > parseFloat(product.price_min)
        }))
    };
  }

  render(terms, { products, suggestions, collections, total }) {

    if (!products.length && !suggestions.length && !collections.length) {
      this.querySelector('[data-state="no-results"] [data-field="terms"]').textContent = terms;
      this.showState('no-results');
      this.status.textContent = `No results for ${terms}`;
      return;
    }

    this.renderList('suggestions', suggestions, item => {
      const node = this.cloneTemplate('term');
      const link = node.querySelector('a');

      link.href = item.url;
      link.dataset.searchTerm = item.text;
      node.querySelector('[data-field="text"]').innerHTML = item.html;

      return node;
    });

    this.renderList('collections', collections, item => {
      const node = this.cloneTemplate('collection');
      const link = node.querySelector('a');

      link.href = item.url;
      link.textContent = item.text;

      return node;
    });

    this.renderList('products', products, item => {
      const node = this.cloneTemplate('product');
      const image = node.querySelector('[data-field="image"]');

      node.querySelector('a').href = item.url;
      node.querySelector('[data-field="title"]').innerHTML = item.titleHtml;
      node.querySelector('[data-field="price"]').textContent = item.from ? `From ${item.price}` : item.price;

      if (item.image) {
        image.src = resizeImage(item.image, 300);
        image.srcset = `${resizeImage(item.image, 300)} 1x, ${resizeImage(item.image, 600)} 2x`;
        image.alt = item.imageAlt;
      } else {
        image.remove();
      }

      return node;
    });

    // Products take the full width when there are no suggestions or categories.
    this.querySelector('[data-aside]').classList.toggle('hidden', !suggestions.length && !collections.length);

    const viewAll = this.querySelector('[data-view-all]');

    viewAll.href = searchUrl(terms);
    viewAll.dataset.searchTerm = terms;
    viewAll.textContent = typeof total === 'number' && total > 0
      ? `View all ${total.toLocaleString('en-US')} ${total === 1 ? 'result' : 'results'}`
      : 'View all results';

    this.showState('results');
    this.scroller.scrollTop = 0;

    this.status.textContent = typeof total === 'number'
      ? `${total.toLocaleString('en-US')} ${total === 1 ? 'product' : 'products'} found`
      : `${products.length} ${products.length === 1 ? 'product' : 'products'} found`;
  }

  renderRecent() {
    this.renderList('recent', recentSearches.get().map(text => ({ text, url: searchUrl(text) })), item => {
      const node = this.cloneTemplate('term');
      const link = node.querySelector('a');

      link.href = item.url;
      link.dataset.searchTerm = item.text;
      node.querySelector('[data-field="text"]').textContent = item.text;

      return node;
    });
  }

  /**
   * Fill a section's list and hide the section when there is nothing to show.
   *
   * @param {string} name - `data-section` value
   * @param {Array} items
   * @param {function(Object): HTMLElement} build
   */
  renderList(name, items, build) {
    const section = this.querySelector(`[data-section="${name}"]`);
    const list = section.querySelector('[data-list]');

    list.replaceChildren(...items.map(item => {
      const node = build(item);

      node.id = `${this.input.id}-option-${++this.optionId}`;

      return node;
    }));

    section.classList.toggle('hidden', !items.length);
  }

  cloneTemplate(name) {
    return this.querySelector(`template[data-template="${name}"]`).content.firstElementChild.cloneNode(true);
  }

  /**
   * Options in reading order, skipping hidden states and sections.
   *
   * @returns {HTMLElement[]}
   */
  get options() {
    // Laid-out options only: hidden states, empty sections and the rows cut on phones drop out.
    return Array.from(this.listbox.querySelectorAll('[role="option"]')).filter(option => option.getClientRects().length > 0);
  }

  setActive(option) {
    this.querySelectorAll('[role="option"][aria-selected="true"]').forEach(item => item.setAttribute('aria-selected', 'false'));

    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(event) {

    if (event.key === 'Escape') {
      if (this.isOpen) {
        // Escape would also clear a search field, and the resulting input event reopen the panel.
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
      return;
    }

    if (event.key === 'Tab') {
      this.close();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

    if (!this.isOpen) {
      if (event.key !== 'Enter') this.open();
      return;
    }

    const options = this.options;
    const current = options.findIndex(option => option.getAttribute('aria-selected') === 'true');

    if (event.key === 'Enter') {
      if (current === -1) return; // Submit the form as before.

      event.preventDefault();
      options[current].querySelector('a').click();
      return;
    }

    event.preventDefault();

    if (!options.length) return;

    const next = event.key === 'ArrowDown'
      ? (current + 1) % options.length
      : (current <= 0 ? options.length : current) - 1;

    this.setActive(options[next]);
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
